import React, { useMemo, useState } from 'react';
import { Calendar, Clock, Trash2, PlusCircle, Edit3, Copy } from 'lucide-react';
import ModalDrawer from './ModalDrawer.js';
import HowItWorksButton from './explain/HowItWorksButton';
import { getEffectiveSnapshotDate, parseDateInputToLocalDate } from '../utils/snapshots';
import { PRO_SNAPSHOT_LIMIT } from '../config/billing';

const formatDate = (value, locale) => {
    if (!value) return '';
    try {
        if (value.toDate) {
            return value.toDate().toLocaleDateString(locale || undefined, { dateStyle: 'medium' });
        }
        if (value instanceof Date) {
            return value.toLocaleDateString(locale || undefined, { dateStyle: 'medium' });
        }
        return new Date(value).toLocaleDateString(locale || undefined, { dateStyle: 'medium' });
    } catch {
        return '';
    }
};

const toLocalDateInput = (value) => {
    if (!value) return '';
    let date;
    try {
        if (value.toDate) {
            date = value.toDate();
        } else if (value instanceof Date) {
            date = value;
        } else {
            const parsed = new Date(value);
            if (isNaN(parsed.getTime())) return '';
            date = parsed;
        }
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    } catch {
        return '';
    }
};

const SnapshotModal = ({
    isOpen,
    onClose,
    snapshots = [],
    onCreateSnapshot,
    onSelectSnapshot,
    onDuplicateSnapshot,
    onDeleteSnapshot,
    onUpdateSnapshotDate,
    onViewNow,
    currentSnapshotId,
    theme = 'light',
    locale,
    t = (key) => key,
    isPro = false,
    freeLimit = 3,
    onUpgrade
}) => {
    const [selectedDate, setSelectedDate] = useState(() => {
        return toLocalDateInput(new Date());
    });
    const [editingSnapshotId, setEditingSnapshotId] = useState(null);
    const [editingName, setEditingName] = useState('');
    const [editingDate, setEditingDate] = useState('');
    const [selectedName, setSelectedName] = useState('');

    const createDateInputRef = React.useRef(null);
    const editingDateInputRef = React.useRef(null);

    const openDatePicker = (ref) => {
        if (!ref || !ref.current) return;
        const input = ref.current;
        input.focus();
        if (typeof input.showPicker === 'function') {
            input.showPicker();
        }
    };

    const sortedSnapshots = useMemo(() => {
        return [...snapshots].sort((a, b) => {
            const aDate = getEffectiveSnapshotDate(a);
            const bDate = getEffectiveSnapshotDate(b);
            const aTime = aDate ? aDate.getTime() : 0;
            const bTime = bDate ? bDate.getTime() : 0;
            return bTime - aTime;
        });
    }, [snapshots]);

    React.useEffect(() => {
        if (editingSnapshotId) {
            const snap = snapshots.find(s => s.id === editingSnapshotId);
            if (snap) {
                setEditingName(snap.name || '');
                setEditingDate(toLocalDateInput(snap.snapshotDate || snap.createdAt));
            }
        }
    }, [editingSnapshotId, snapshots]);

    const handleCreate = () => {
        const parsed = selectedDate ? parseDateInputToLocalDate(selectedDate) : null;
        const dateValue = parsed || new Date();
        const nameValue = typeof selectedName === 'string' ? selectedName.trim() : '';
        onCreateSnapshot(dateValue, nameValue || null);
        setSelectedDate(toLocalDateInput(new Date()));
        setSelectedName('');
    };

    const handleSaveEditing = (snapId) => {
        if (snapId && onUpdateSnapshotDate) {
            onUpdateSnapshotDate(snapId, editingDate, editingName);
            setEditingSnapshotId(null);
        }
    };

    return (
        <ModalDrawer
            isOpen={isOpen}
            onClose={onClose}
            title={t('snapshots.title', 'Snapshots')}
            theme={theme}
            headerActions={
                <>
                    <HowItWorksButton feature="snapshots" />
                    {currentSnapshotId ? (
                        <button
                            onClick={onViewNow}
                            className="btn btn-grad"
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}
                        >
                            <Clock size={16} /> {t('snapshots.returnToNow', 'Back to now')}
                        </button>
                    ) : null}
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                    {t('snapshots.countLabel', 'Snapshots: {{count}} / {{limit}}', { count: snapshots.length, limit: isPro ? PRO_SNAPSHOT_LIMIT : freeLimit })}
                </div>

                {!isPro && snapshots.length >= freeLimit && (
                    <div className="snapshot-upgrade-hint">
                        <span>{t('snapshots.freeLimitHint', { limit: freeLimit, proLimit: PRO_SNAPSHOT_LIMIT })}</span>
                        <button className="btn btn-grad btn-sm" onClick={onUpgrade}>
                            {t('snapshots.upgradeCta', { proLimit: PRO_SNAPSHOT_LIMIT })}
                        </button>
                    </div>
                )}

                <div style={{ padding: '16px', background: 'var(--accent-bg)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--accent-border)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                        {t('snapshots.createTitle', 'Create new snapshot')}
                    </div>
                    <div className="form-group form-group--reduced-margin">
                        <label className="form-label">
                            {t('snapshots.nameOptional', 'Snapshot name (optional)')}
                        </label>
                        <input
                            type="text"
                            value={selectedName}
                            onChange={(e) => setSelectedName(e.target.value)}
                            className="form-input"
                            placeholder={t('snapshots.namePlaceholder', 'Snapshot name')}
                        />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label">{t('snapshots.selectDate', 'Snapshot date')}</label>
                        <div className="snapshot-create-form">
                            <div className="input-with-icon" style={{ width: '100%' }}>
                                <button

                                    type="button"

                                    className="input-icon"

                                    onClick={() => openDatePicker(createDateInputRef)}

                                    aria-label={t('itemForm.openCalendar')}

                                    style={{ cursor: 'pointer', pointerEvents: 'auto', background: 'none', border: 0, padding: 0, color: 'inherit', display: 'grid' }}

                                >

                                    <Calendar size={18} aria-hidden="true" />

                                </button>
                                <input
                                    ref={createDateInputRef}
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    className="form-input"
                                />
                            </div>
                            <button
                                onClick={handleCreate}
                                className="btn btn-grad"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                <PlusCircle size={18} /> {t('snapshots.create', 'Create Snapshot')}
                            </button>
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                        {t('snapshots.listTitle', 'Snapshot list')}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '60vh', overflowY: 'auto' }}>
                    {sortedSnapshots.length === 0 && (
                        <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>
                            {t('snapshots.empty', 'No snapshots yet')}
                        </div>
                    )}
                    {sortedSnapshots.map((snap) => {
                        const effectiveDate = getEffectiveSnapshotDate(snap);
                        const created = formatDate(effectiveDate, locale);
                        const isActive = currentSnapshotId === snap.id;
                        const isEditing = editingSnapshotId === snap.id;
                        return (
                            <div
                                key={snap.id}
                                className={`snapshot-card${isActive ? ' snapshot-card--active' : ''}`}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1 }}>
                                        {!isEditing ? (
                                            <>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, marginBottom: '8px', flexWrap: 'wrap' }}>
                                                    <span>{snap.name || created || t('snapshots.unnamed', 'Unnamed')}</span>
                                                    {isActive && (
                                                        <span className="snapshot-pill snapshot-pill--active">
                                                            {t('snapshots.active', 'Active')}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="snapshot-meta-row">
                                                    <span className="snapshot-date">
                                                        <Calendar size={14} /> {created || '-'}
                                                    </span>
                                                </div>
                                            </>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                <input
                                                    type="text"
                                                    value={editingName}
                                                    onChange={(e) => setEditingName(e.target.value)}
                                                    className="form-input"
                                                    placeholder={t('snapshots.namePlaceholder', 'Snapshot name')}
                                                />
                                                <div className="input-with-icon">
                                                    <button

                                                        type="button"

                                                        className="input-icon"

                                                        onClick={() => openDatePicker(editingDateInputRef)}

                                                        aria-label={t('itemForm.openCalendar')}

                                                        style={{ cursor: 'pointer', pointerEvents: 'auto', background: 'none', border: 0, padding: 0, color: 'inherit', display: 'grid' }}

                                                    >

                                                        <Calendar size={18} aria-hidden="true" />

                                                    </button>
                                                    <input
                                                        ref={editingDateInputRef}
                                                        type="date"
                                                        value={editingDate}
                                                        onChange={(e) => setEditingDate(e.target.value)}
                                                        className="form-input"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        {!isEditing ? (
                                            <>
                                                {!isActive && (
                                                    <button
                                                        onClick={() => onSelectSnapshot(snap.id)}
                                                        className="btn btn-grad"
                                                        style={{ fontSize: '0.8125rem' }}
                                                    >
                                                        {t('snapshots.select', 'Select')}
                                                    </button>
                                                )}
                                                {onDuplicateSnapshot && (
                                                    <button
                                                        onClick={() => onDuplicateSnapshot(snap)}
                                                        className="btn btn-ghost"
                                                        style={{ padding: '8px' }}
                                                        title={t('snapshots.duplicate', 'Duplicate snapshot')}
                                                        aria-label={t('snapshots.duplicate', 'Duplicate snapshot')}
                                                    >
                                                        <Copy size={16} />
                                                    </button>
                                                )}
                                                <button onClick={() => setEditingSnapshotId(snap.id)} className="btn btn-ghost" style={{ padding: '8px' }} title={t('general.edit')} aria-label={t('general.edit')}>
                                                    <Edit3 size={16} />
                                                </button>
                                                <button onClick={() => onDeleteSnapshot(snap.id)} className="btn btn-ghost" style={{ color: 'var(--danger-text)', padding: '8px' }} title={t('general.delete')} aria-label={t('general.delete')}>
                                                    <Trash2 size={16} />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button onClick={() => setEditingSnapshotId(null)} className="btn btn-ghost btn-sm">
                                                    {t('general.cancel')}
                                                </button>
                                                <button onClick={() => handleSaveEditing(snap.id)} className="btn btn-grad btn-sm">
                                                    {t('general.save')}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    </div>
                </div>
            </div>
        </ModalDrawer>
    );
};

export default SnapshotModal;
